import { useState, useEffect, useRef, useCallback } from 'react';
import { Brain, CircleAlert as AlertCircle, Copy, Check, ChevronDown, ChevronUp, Users, MessageSquareText, Shield, TriangleAlert as AlertTriangle } from 'lucide-react';
import { useApi } from '../../lib/useApi';
import type { Account, ValueStatements, TargetPersona, VendorIntelligence } from '../../types';
import LoadingSpinner from '../common/LoadingSpinner';
import FormattedText from '../common/FormattedText';

const PERSONA_LOAD_RETRY_DELAYS_MS = [1000, 3000];

interface Props {
  account: Account;
  valueStatements: ValueStatements | null;
  onValueStatementsChange?: (vs: ValueStatements | null) => void;
  onAccountRefresh?: () => void;
  vendorIntelPublished: boolean;
  pyramidCompleted: boolean;
  showPersonaSections?: boolean;
  personaMessagingView?: boolean;
}

type VSStatus = 'pending' | 'processing' | 'completed' | 'error' | 'skipped';

const VS_STAGE_LABELS: Record<number, string> = {
  0: 'Assembling inputs',
  1: 'Assembling inputs',
  2: 'Generating value statements',
  3: 'Mapping execution pressures',
  4: 'Building persona statements',
  5: 'Evaluating quality',
  6: 'Summarising',
};

const PERSONA_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  E: { bg: 'bg-app-focus/10', text: 'text-app-focus', border: 'border-app-focus' },
  C: { bg: 'bg-forest-50', text: 'text-forest-700', border: 'border-forest-200' },
  S: { bg: 'bg-app-focus/10', text: 'text-app-focus', border: 'border-app-focus/30' },
  P: { bg: 'bg-app-warning/10', text: 'text-app-warning', border: 'border-app-warning' },
  V: { bg: 'bg-app-critical/10', text: 'text-app-critical', border: 'border-app-critical' },
  H: { bg: 'bg-app-focus/10', text: 'text-app-focus', border: 'border-app-focus' },
  D: { bg: 'bg-app-warning/10', text: 'text-app-warning', border: 'border-app-warning' },
  F: { bg: 'bg-app-positive/10', text: 'text-app-positive', border: 'border-app-positive' },
};

function getPersonaColor(initial: string) {
  return PERSONA_COLORS[initial?.toUpperCase()] || PERSONA_COLORS.E;
}

export default function ValueStatementsSection({
  account,
  valueStatements: initialVS,
  onValueStatementsChange,
  onAccountRefresh,
  vendorIntelPublished,
  pyramidCompleted,
  showPersonaSections = true,
  personaMessagingView = false,
}: Props) {
  const { apiGet } = useApi();
  const [personaProfile, setPersonaProfile] = useState<{ orgId: string; personas: TargetPersona[]; established: boolean } | null>(null);
  const currentProfile = personaProfile?.orgId === account.org_id ? personaProfile : null;
  const targets = currentProfile?.personas ?? null;
  // An organisation that has not established personas yet keeps its existing messaging visible;
  // only a non-empty list, or one an admin deliberately emptied, filters it.
  const personasPending = !!currentProfile && !currentProfile.established;
  const [targetLoadFailed, setTargetLoadFailed] = useState(false);
  const [personaLoadRequest, setPersonaLoadRequest] = useState(0);
  const [vs, setVs] = useState<ValueStatements | null>(initialVS);
  const sectionTitle = personaMessagingView ? 'Persona Messaging' : 'Value Statements';
  const sectionDescription = personaMessagingView
    ? `Persona impact mapping and persona-specific value statements for ${account.company_name}`
    : `Persona-specific enterprise value statements for ${account.company_name}`;
  const canShowPersonaSections = personaMessagingView || showPersonaSections;
  const [copied, setCopied] = useState(false);
  const [expandedVerbose, setExpandedVerbose] = useState(false);
  const onChangeRef = useRef(onValueStatementsChange);
  onChangeRef.current = onValueStatementsChange;
  const onRefreshRef = useRef(onAccountRefresh);
  onRefreshRef.current = onAccountRefresh;

  useEffect(() => {
    setVs(initialVS);
  }, [initialVS]);

  // Load value statements on mount to detect if research is already running.
  // Reuses the signed GET /v1/account/{id}/value read (same row the parent tab fetches).
  useEffect(() => {
    let cancelled = false;
    const loadValueStatements = async () => {
      const { value_statements: data } = await apiGet<{ value_statements: ValueStatements | null }>(
        `/v1/account/${account.id}/value?org_id=${encodeURIComponent(account.org_id)}`,
      );
      if (data && !cancelled) {
        setVs(data);
        onChangeRef.current?.(data);
      }
    };
    loadValueStatements().catch(() => { /* keep the prop-provided value statements */ });
    return () => { cancelled = true; };
  }, [account.id, account.org_id, apiGet]);

  // Persona rows stay hidden until the organisation's personas are known, so a transient
  // failure is retried before asking the user to retry by hand.
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setPersonaProfile(null);
    setTargetLoadFailed(false);
    const load = (attempt: number) => {
      apiGet<{ intelligence: VendorIntelligence | null }>(`/v1/vendor-intelligence?org_id=${encodeURIComponent(account.org_id)}`)
        .then(result => {
          if (cancelled) return;
          const personas = result.intelligence?.target_personas || [];
          setPersonaProfile({ orgId: account.org_id, personas, established: personas.length > 0 || !!result.intelligence?.target_personas_customized });
        })
        .catch(() => {
          if (cancelled) return;
          if (attempt < PERSONA_LOAD_RETRY_DELAYS_MS.length) timer = setTimeout(() => load(attempt + 1), PERSONA_LOAD_RETRY_DELAYS_MS[attempt]);
          else setTargetLoadFailed(true);
        });
    };
    load(0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [account.org_id, apiGet, personaLoadRequest]);

  const status: VSStatus = (vs?.status as VSStatus) || 'pending';
  const processing = status === 'processing';
  const showLoading = processing;

  const copyToClipboard = async () => {
    if (!vs || vs.status !== 'completed' || !vs.summary) return;
    const s = summary;
    if (!s) return;
    const lines: string[] = [];

    if (s.strategic_synthesis) {
      lines.push('STRATEGIC SYNTHESIS');
      lines.push(s.strategic_synthesis.dominant_theme);
      lines.push('');
      lines.push(s.strategic_synthesis.supporting_context);
      lines.push('');
    }

    if (s.execution_pressures?.length) {
      lines.push('ENTERPRISE EXECUTION PRESSURES');
      s.execution_pressures.forEach((ep) => {
        lines.push(`${String(ep.number).padStart(2, '0')}. ${ep.title}`);
        lines.push(ep.strategic_context);
        lines.push(ep.execution_pressure);
        lines.push(`Vendor Alignment: ${ep.vendor_alignment}`);
        lines.push('');
      });
    }

    if (s.persona_impact_map?.length) {
      lines.push('PERSONA IMPACT MAPPING');
      s.persona_impact_map.forEach((p) => {
        lines.push(`${p.persona}`);
        lines.push(`  Accountability: ${p.strategic_accountability}`);
        lines.push(`  Risk: ${p.executive_risk}`);
        lines.push(`  Vendor Capability: ${p.vendor_capability}`);
        lines.push('');
      });
    }

    if (s.value_statements?.length) {
      lines.push('VALUE STATEMENTS');
      s.value_statements.forEach((v) => {
        lines.push(`[${v.persona}]`);
        lines.push(v.statement_text);
        lines.push(`Proof Point: ${v.proof_point}`);
        lines.push('');
      });
    }

    await navigator.clipboard.writeText(lines.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const completed = status === 'completed';
  const hasError = status === 'error';
  // Not a failure: generation never ran because a prerequisite is missing. Shown as guidance
  // rather than an error, since the fix is a setup step and not an investigation.
  const isBlocked = status === 'skipped';
  const allowed = (title: unknown) => personasPending || (typeof title === 'string' && !!targets?.some(p => [p.title, ...(p.aliases || [])].some(t => t.trim().toLowerCase() === title.trim().toLowerCase())));
  const originalSummary = vs?.summary;
  const summary = originalSummary ? { ...originalSummary,
    persona_impact_map: originalSummary.persona_impact_map?.filter(row => allowed(row.persona)),
    value_statements: originalSummary.value_statements?.filter(row => allowed(row.persona)),
  } : originalSummary;
  const stalePersonas = targets !== null && !!originalSummary?.value_statements?.some(row => !allowed(row.persona));

  return (
    <div className="space-y-6">
      <div className="border-t border-app-line pt-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-app-ink">{sectionTitle}</h2>
            <p className="text-sm text-app-muted mt-0.5">
              {sectionDescription}
            </p>
          </div>
          {completed && (
            <button onClick={copyToClipboard} className="btn-ghost !py-2.5 !px-4 !text-sm">
              {copied
                ? <><Check className="w-4 h-4 mr-2 text-app-positive" /><span className="text-app-positive">Copied</span></>
                : <><Copy className="w-4 h-4 mr-2" />Copy Summary</>
              }
            </button>
          )}
        </div>
      </div>

      {stalePersonas && <p role="status" className="text-sm text-app-warning rounded-xl border border-app-warning/30 p-4">Your organisation's target personas have changed. Rerun account research from Overview to refresh the messaging.</p>}
      {personasPending && canShowPersonaSections && <p role="status" className="text-sm text-app-muted rounded-xl border border-app-line p-4">Your organisation's target personas are not set up yet, so these personas are unconfirmed. An admin can rerun Organisation Intelligence or add target personas, then rerun account research.</p>}
      {!currentProfile && !targetLoadFailed && completed && canShowPersonaSections && <p role="status" className="text-sm text-app-muted">Loading your organisation's target personas...</p>}
      {targetLoadFailed && (
        <div role="alert" className="flex flex-wrap items-center gap-3 text-sm text-app-warning">
          Could not load your organisation's target personas, so persona messaging is hidden.
          <button type="button" onClick={() => setPersonaLoadRequest(n => n + 1)} className="btn-ghost !py-1.5 !px-3 !text-sm">Try again</button>
        </div>
      )}
      {showLoading && <VSProgressIndicator currentStage={vs?.current_stage || 0} />}

      {!showLoading && completed && summary && (
        <>
          {!personaMessagingView && summary.strategic_synthesis && (
            <StrategicSynthesisCard synthesis={summary.strategic_synthesis} />
          )}

          {!personaMessagingView && summary.execution_pressures && summary.execution_pressures.length > 0 && (
            <ExecutionPressuresSection pressures={summary.execution_pressures} />
          )}

          {canShowPersonaSections && summary.persona_impact_map && summary.persona_impact_map.length > 0 && (
            <PersonaImpactTable rows={summary.persona_impact_map} />
          )}

          {canShowPersonaSections && summary.value_statements && summary.value_statements.length > 0 && (
            <ValueStatementsCards statements={summary.value_statements} />
          )}

          {vs?.raw_verbose_output && (
            <div className="bg-app-surface rounded-lg border border-app-line shadow-none">
              <button
                onClick={() => setExpandedVerbose(!expandedVerbose)}
                className="w-full flex items-center justify-between p-5 text-left hover:bg-app-canvas transition-colors rounded-lg"
              >
                <div className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-app-muted" />
                  <span className="text-sm font-semibold text-app-ink">Full Value Statement Research Output</span>
                </div>
                {expandedVerbose
                  ? <ChevronUp className="w-4 h-4 text-app-muted" />
                  : <ChevronDown className="w-4 h-4 text-app-muted" />
                }
              </button>
              {expandedVerbose && (
                <div className="px-5 pb-5 border-t border-app-line">
                  <div className="mt-4 max-w-none text-app-muted text-xs">
                    <FormattedText text={vs.raw_verbose_output} />
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {!showLoading && hasError && (
        <div className="bg-app-critical/10 border border-app-critical rounded-lg p-8 text-center">
          <AlertCircle className="w-12 h-12 text-app-critical mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-app-critical mb-2">Generation Failed</h3>
          <p className="text-sm text-app-critical mb-4">
            {vs?.error_message || 'Something went wrong during value statement generation. Please re-run from the Overview tab.'}
          </p>
        </div>
      )}

      {!showLoading && isBlocked && (
        <div className="bg-app-warning/10 border border-app-warning rounded-lg p-8 text-center">
          <AlertCircle className="w-12 h-12 text-app-warning mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-app-warning mb-2">Waiting on a prerequisite</h3>
          <p className="text-sm text-app-warning mb-4">
            {vs?.error_message || 'Value statement generation was skipped because a prerequisite is not yet met.'}
          </p>
        </div>
      )}

      {!vs && !showLoading && !hasError && !isBlocked && (
        <div className="bg-app-canvas border border-app-line rounded-lg p-12 text-center">
          <MessageSquareText className="w-12 h-12 text-app-line-strong mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-app-ink mb-2">Value Statements Pending</h3>
          <p className="text-sm text-app-muted mb-6 max-w-md mx-auto">
            Value statements will be generated automatically as part of the research pipeline.
          </p>
        </div>
      )}
    </div>
  );
}

function VSProgressIndicator({ currentStage }: { currentStage: number }) {
  const stages = [
    { num: 1, label: 'Inputs' },
    { num: 2, label: 'Strategy' },
    { num: 3, label: 'Pressures' },
    { num: 4, label: 'Statements' },
    { num: 5, label: 'Quality' },
    { num: 6, label: 'Summary' },
  ];

  return (
    <div className="rounded-lg border border-app-line bg-app-surface p-5 sm:p-8">
      <div className="flex items-center gap-3 mb-6">
        <Brain className="w-6 h-6 text-forest-600 animate-pulse" />
        <div>
          <h3 className="text-base font-semibold text-app-ink">Generating value statements</h3>
          <p className="text-sm text-app-muted">{VS_STAGE_LABELS[currentStage] || 'Processing...'} This typically takes 90 seconds to 2 minutes. You can leave this page while it runs.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {stages.map((stage) => {
          const isComplete = currentStage > stage.num;
          const isCurrent = currentStage === stage.num;
          return (
            <div key={stage.num} className="flex-1">
              <div className="h-2.5 rounded-full overflow-hidden bg-app-line" role="progressbar" aria-label={stage.label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={isComplete ? 100 : isCurrent ? 50 : 0}>
                <div
                  className={`h-full rounded-full transition duration-700 ${
                    isComplete ? 'bg-app-positive' :
                    isCurrent ? 'bg-app-accent animate-pulse' :
                    ''
                  }`}
                  style={{ width: isComplete ? '100%' : isCurrent ? '50%' : '0%' }}
                />
              </div>
              <p className={`text-xs mt-2 text-center ${
                isComplete ? 'text-app-positive font-medium' :
                isCurrent ? 'text-app-ink font-medium' :
                'text-app-muted'
              }`}>
                {stage.label}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StrategicSynthesisCard({ synthesis }: { synthesis: { dominant_theme: string; supporting_context: string } }) {
  return (
    <div className="relative overflow-hidden rounded-lg border border-forest-200 bg-app-surface shadow-none">
      <div className="absolute top-0 left-0 w-1.5 h-full bg-app-accent" />
      <div className="p-6 pl-8">
        <div className="flex items-center gap-2 mb-4">
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-forest-100 rounded-full">
            <span className="text-xs font-bold text-forest-700 tracking-wider">STEP 01</span>
          </div>
          <h3 className="text-sm font-bold text-forest-800 uppercase tracking-wider">Strategic Synthesis</h3>
        </div>
        <div className="space-y-3">
          <div>
            <p className="text-xs font-semibold text-forest-600 uppercase tracking-wider mb-1.5">Dominant Theme</p>
            <p className="text-sm text-app-ink leading-relaxed">{synthesis.dominant_theme}</p>
          </div>
          <p className="text-sm text-app-muted leading-relaxed">{synthesis.supporting_context}</p>
        </div>
      </div>
    </div>
  );
}

function ExecutionPressuresSection({ pressures }: { pressures: { number: number; title: string; strategic_context: string; execution_pressure: string; vendor_alignment: string }[] }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5 px-2.5 py-1 bg-app-focus/10 rounded-full">
          <span className="text-xs font-bold text-app-focus tracking-wider">STEP 02</span>
        </div>
        <h3 className="text-sm font-bold text-app-focus uppercase tracking-wider">Enterprise Execution Pressures</h3>
      </div>
      <div className="grid gap-4">
        {pressures.map((ep) => (
          <div key={ep.number} className="bg-app-surface rounded-lg border border-app-line shadow-none p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-app-focus/10 rounded-lg flex items-center justify-center">
                <span className="text-lg font-bold text-app-focus">{String(ep.number).padStart(2, '0')}</span>
              </div>
              <div className="flex-1 space-y-3">
                <h4 className="text-sm font-bold text-app-ink">{ep.title}</h4>
                <p className="text-sm text-app-ink leading-relaxed">{ep.strategic_context}</p>
                <div className="flex items-start gap-2 p-3 bg-app-warning/10 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-app-warning flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-app-warning leading-relaxed">{ep.execution_pressure}</p>
                </div>
                <div className="flex items-start gap-2 p-3 bg-forest-50/50 rounded-lg">
                  <Shield className="w-4 h-4 text-forest-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-forest-700 leading-relaxed">{ep.vendor_alignment}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PersonaImpactTable({ rows }: { rows: { persona: string; strategic_accountability: string; executive_risk: string; vendor_capability: string }[] }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5 px-2.5 py-1 bg-app-focus/10 rounded-full">
          <span className="text-xs font-bold text-app-focus tracking-wider">STEP 03</span>
        </div>
        <h3 className="text-sm font-bold text-app-focus uppercase tracking-wider">Persona Impact Mapping</h3>
      </div>
      <div className="bg-app-surface rounded-lg border border-app-line shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-app-canvas border-b border-app-line">
                <th className="text-left text-xs font-semibold text-app-muted uppercase tracking-wider px-5 py-3">Persona</th>
                <th className="text-left text-xs font-semibold text-app-muted uppercase tracking-wider px-5 py-3">Strategic Accountability</th>
                <th className="text-left text-xs font-semibold text-app-muted uppercase tracking-wider px-5 py-3">Executive Risk if Uncontrolled</th>
                <th className="text-left text-xs font-semibold text-app-muted uppercase tracking-wider px-5 py-3">Vendor Capability</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-app-line">
              {rows.map((row, i) => (
                <tr key={i} className="hover:bg-app-canvas/50 transition-colors">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-app-focus flex-shrink-0" />
                      <span className="text-sm font-semibold text-app-ink">{row.persona}</span>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <p className="text-sm text-app-ink leading-relaxed">{row.strategic_accountability}</p>
                  </td>
                  <td className="px-5 py-4">
                    <p className="text-sm text-app-critical leading-relaxed">{row.executive_risk}</p>
                  </td>
                  <td className="px-5 py-4">
                    <p className="text-sm text-forest-700 leading-relaxed">{row.vendor_capability}</p>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ValueStatementsCards({ statements }: { statements: { persona: string; persona_initial: string; statement_text: string; proof_point: string }[] }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5 px-2.5 py-1 bg-app-ink rounded-full">
          <span className="text-xs font-bold text-white tracking-wider">STEP 04</span>
        </div>
        <h3 className="text-sm font-bold text-app-ink uppercase tracking-wider">Persona-Specific Value Statements</h3>
      </div>
      <div className="grid gap-4">
        {statements.map((stmt, i) => {
          const color = getPersonaColor(stmt.persona_initial);
          return (
            <div key={i} className={`rounded-lg border shadow-none overflow-hidden ${color.border}`}>
              <div className={`${color.bg} px-6 py-4 flex items-center gap-3 border-b ${color.border}`}>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-app-surface shadow-none`}>
                  <span className={`text-lg font-bold ${color.text}`}>{stmt.persona_initial}</span>
                </div>
                <h4 className={`text-sm font-bold ${color.text}`}>{stmt.persona}</h4>
              </div>
              <div className="bg-app-surface px-6 py-5">
                <FormattedText text={stmt.statement_text} className="text-sm text-app-ink" />
              </div>
              <div className="bg-app-canvas px-6 py-4 border-t border-app-line">
                <div className="flex items-start gap-2">
                  <span className="text-xs font-bold text-app-muted uppercase tracking-wider whitespace-nowrap mt-0.5">Proof Point</span>
                  <p className="text-sm text-app-muted leading-relaxed">{stmt.proof_point}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
